import { Play, CheckCircle2, RotateCcw, MoreVertical, Clock, Link2, Flag, Users } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { founderChip, priorityBadge, isOverdue, taskMentions, ukToday } from "@/data/taskOptions";

export const TaskCard = ({ task, relatedNames = {}, canWrite, onOpen, onStart, onComplete, onReopen, draggable, onDragStart, onDragOver, onDrop }) => {
  const overdue = isOverdue(task);
  const milestoneName = task.related_milestone_id ? relatedNames[task.related_milestone_id] : null;
  const mentions = taskMentions(task);
  const dueToday = task.due_date === ukToday() && !["Complete", "Cancelled"].includes(task.status);

  return (
    <div
      data-testid={`task-card-${task.id}`}
      draggable={draggable && canWrite}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={() => onOpen(task)}
      className={`group relative overflow-hidden cursor-pointer rounded-xl border bg-white p-3 shadow-[0_1px_6px_rgba(20,36,95,0.05)] transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_6px_18px_rgba(20,36,95,0.10)] ${overdue ? "border-[#FB6F5A]/50" : "border-[#E4E9F3]"}`}
    >
      {task.status === "Complete" && (
        <div data-testid={`task-completed-banner-${task.id}`} className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="w-[160%] -rotate-[7deg] bg-[#16A34A]/50 py-1 text-center text-xs font-extrabold uppercase tracking-[0.2em] text-white shadow-sm">Completed</div>
        </div>
      )}
      {task.status === "Cancelled" && (
        <div data-testid={`task-cancelled-banner-${task.id}`} className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="w-[160%] -rotate-[7deg] bg-[#94A3B8]/50 py-1 text-center text-xs font-extrabold uppercase tracking-[0.2em] text-white shadow-sm">Cancelled</div>
        </div>
      )}
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <p className="line-clamp-2 flex-1 text-sm font-semibold leading-snug text-[#14245F]">{task.title}</p>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${founderChip(task.assigned_to)}`}>{task.assigned_to}</span>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <StatusBadge status={priorityBadge(task.priority)} label={task.priority} data-testid={`task-priority-${task.id}`} />
        <span className="rounded-full bg-[#EEF2FB] px-2 py-0.5 text-[10px] font-semibold text-[#5A6B8C]">{task.workstream}</span>
        {dueToday && (
          <span data-testid={`task-due-today-${task.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#06B6D4]/12 px-2 py-0.5 text-[10px] font-bold text-[#0E7490]"><Clock size={10} /> Due today</span>
        )}
        {task.assigned_to === "Both" && (
          <span data-testid={`task-shared-${task.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#E0A82E]/15 px-2 py-0.5 text-[10px] font-bold text-[#B8791A]"><Users size={10} /> Shared</span>
        )}
        {mentions.map((m) => (
          <span key={m} data-testid={`task-flag-${task.id}-${m}`} className="inline-flex items-center gap-1 rounded-full bg-[#E0A82E]/15 px-2 py-0.5 text-[10px] font-bold text-[#B8791A]"><Flag size={10} /> {m}</span>
        ))}
      </div>

      {milestoneName && (
        <p className="mb-2 flex items-center gap-1 truncate text-[11px] font-medium text-[#0C8E8D]" data-testid={`task-supports-${task.id}`}>
          <Link2 size={11} /> Supports: {milestoneName}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${overdue ? "text-[#C2410C]" : "text-[#5A6B8C]"}`}>
          <Clock size={11} />
          {task.due_date ? `${task.due_date}${task.due_time ? ` ${task.due_time}` : ""}` : "No deadline"}
          {overdue && " · overdue"}
        </span>
        {canWrite && (
          <div className="flex items-center gap-0.5 opacity-70 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
            {task.status === "To do" && (
              <Button size="icon" variant="ghost" data-testid={`task-start-${task.id}`} onClick={() => onStart(task)} className="h-7 w-7 rounded-full text-[#06B6D4]" title="Start"><Play size={13} /></Button>
            )}
            {task.status !== "Complete" && task.status !== "Cancelled" && (
              <Button size="icon" variant="ghost" data-testid={`task-complete-${task.id}`} onClick={() => onComplete(task)} className="h-7 w-7 rounded-full text-[#16A34A]" title="Mark complete"><CheckCircle2 size={13} /></Button>
            )}
            {task.status === "Complete" && (
              <Button size="icon" variant="ghost" data-testid={`task-reopen-${task.id}`} onClick={() => onReopen(task)} className="h-7 w-7 rounded-full text-[#5A6B8C]" title="Reopen"><RotateCcw size={13} /></Button>
            )}
            <Button size="icon" variant="ghost" data-testid={`task-open-${task.id}`} onClick={() => onOpen(task)} className="h-7 w-7 rounded-full text-[#5A6B8C]" title="Details"><MoreVertical size={13} /></Button>
          </div>
        )}
      </div>
    </div>
  );
};
